import { CONFERENCE_ZONE_TYPE_ID } from "../typings/place-and-zone-type-ids.constant";

/**
 * Kind of selected booking filter in the compatibility matrix.
 * category is a place category for workplaces or a zone category for conference rooms.
 */
export type FilterCompatibilityItemKind =
  | "category"
  | "property"
  | "equipment"
  | "equipmentCategory";

/**
 * One row of the filter matrix for a desk: which selected filter hit on which days.
 */
export type FilterCompatibilityItem = {
  /** Selected filter kind (category, property, equipment, or equipment category). */
  kind: FilterCompatibilityItemKind;
  /** Catalog id of the selected filter. */
  id: number;
  /** Minimum quantity when the user filtered by equipment category quantity. */
  quantity?: number;
  /** Booking days (YYYY-MM-DD) where this desk has the filter item. */
  matchedDays: string[];
};

/**
 * Per-desk filter score and matrix from floor availability.
 * hits count true cells; denominator is selectedItemCount × bookingDayCount.
 */
export type FilterCompatibility = {
  /** Desk or zone inventory this row belongs to. */
  inventoryId: number;
  /** Number of filter×day cells that matched. */
  hits: number;
  /** Selected filter items in the request (matrix rows). */
  selectedItemCount: number;
  /** Booking days in the request (matrix columns). */
  bookingDayCount: number;
  /** Column dates of the matrix, YYYY-MM-DD. */
  bookingDays?: string[];
  /** Per-filter matched days for the selection prompt. */
  items?: FilterCompatibilityItem[];
};

const TEMP_YELLOW = 0xdddd56;
/** 0% green end (exact 0 stays yellow). Soft mint, not near-white. */
const LOW_MATCH_GREEN = 0xd4efe3;
/** 25% match — muted light leaf, still weaker than mid. */
const QUARTER_MATCH_GREEN = 0x9cd6b8;
/** 50% match — calm mid green, not fluorescent. */
const MID_MATCH_GREEN = 0x5cb888;
/** 75% match — noticeably darker than mid. */
const THREE_QUARTER_MATCH_GREEN = 0x2a7a52;
/** 100% match — deep green, darker than 75% but still readable on a desk. */
const HIGH_MATCH_GREEN = 0x166534;

const COMPATIBILITY_GREEN_STOPS = [
  { ratio: 1, color: HIGH_MATCH_GREEN },
  { ratio: 0.75, color: THREE_QUARTER_MATCH_GREEN },
  { ratio: 0.5, color: MID_MATCH_GREEN },
  { ratio: 0.25, color: QUARTER_MATCH_GREEN },
  { ratio: 0, color: LOW_MATCH_GREEN }
] as const;

function clampRatio(value: number): number {
  if (value < 0) {
    return 0;
  }
  if (value > 1) {
    return 1;
  }
  return value;
}

function splitRgb(color: number) {
  return {
    r: (color >> 16) & 0xff,
    g: (color >> 8) & 0xff,
    b: color & 0xff
  };
}

function joinRgb(r: number, g: number, b: number): number {
  return (r << 16) + (g << 8) + b;
}

function lerpColor(startColor: number, endColor: number, amount: number): number {
  const start = splitRgb(startColor);
  const end = splitRgb(endColor);

  return joinRgb(
    Math.round(start.r + (end.r - start.r) * amount),
    Math.round(start.g + (end.g - start.g) * amount),
    Math.round(start.b + (end.b - start.b) * amount)
  );
}

export function getFilterCompatibilityRatio(
  compatibility: FilterCompatibility | undefined
): number | undefined {
  if (!compatibility) {
    return undefined;
  }

  const denominator = compatibility.selectedItemCount * compatibility.bookingDayCount;
  if (denominator <= 0) {
    return 0;
  }

  return clampRatio(compatibility.hits / denominator);
}

export function compatibilityCircleFill(ratio: number): number {
  const safeRatio = clampRatio(ratio);
  if (safeRatio === 0) {
    return TEMP_YELLOW;
  }

  for (let index = 0; index < COMPATIBILITY_GREEN_STOPS.length - 1; index += 1) {
    const current = COMPATIBILITY_GREEN_STOPS[index];
    const next = COMPATIBILITY_GREEN_STOPS[index + 1];

    if (safeRatio <= current.ratio && safeRatio >= next.ratio) {
      const span = current.ratio - next.ratio;
      const relative = span === 0 ? 0 : (current.ratio - safeRatio) / span;
      return lerpColor(current.color, next.color, relative);
    }
  }

  return LOW_MATCH_GREEN;
}

function formatCompatibilityPercent(
  compatibility: FilterCompatibility | undefined
): number | undefined {
  if (!compatibility) {
    return undefined;
  }

  return Math.round((getFilterCompatibilityRatio(compatibility) ?? 0) * 100);
}

function formatCompatibilityLabel(
  compatibility: FilterCompatibility | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
  key: string
): string | undefined {
  const percent = formatCompatibilityPercent(compatibility);
  if (percent === undefined) {
    return undefined;
  }

  return t(key, { percent });
}

export function formatFilterCompatibilityLabel(
  compatibility: FilterCompatibility | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string | undefined {
  return formatCompatibilityLabel(compatibility, t, "_booking_filterCompatibility_percentMatch");
}

/** Selection-prompt summary: names filters, unlike the short desk hover. */
export function formatFilterCompatibilityPromptLabel(
  compatibility: FilterCompatibility | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string | undefined {
  return formatCompatibilityLabel(
    compatibility,
    t,
    "_booking_filterCompatibility_percentFilterMatch"
  );
}

const FILTER_COMPATIBILITY_TINT_VARIANTS = new Set<string>(["available", "temp"]);

/** Grey (disabled) and red (occupied / own booking) stay untinted. Tint is available/temp only. */
export function shouldApplyFilterCompatibilityTint(variant: string | undefined): boolean {
  return variant != null && FILTER_COMPATIBILITY_TINT_VARIANTS.has(variant);
}

/** Place zones stay uncolored in booking; only conference rooms take the match tint. */
export function shouldApplyZoneFilterCompatibilityTint(
  variant: string | undefined,
  zoneTypeId: number | undefined
): boolean {
  return zoneTypeId === CONFERENCE_ZONE_TYPE_ID && shouldApplyFilterCompatibilityTint(variant);
}

export function resolveBookingZoneCompatibilityFill(
  variant: string | undefined,
  zoneTypeId: number | undefined,
  ratio: number | undefined
): number | undefined {
  if (!shouldApplyZoneFilterCompatibilityTint(variant, zoneTypeId) || ratio == null) {
    return undefined;
  }
  return compatibilityCircleFill(ratio);
}
